import React, { useEffect, useState } from 'react';
import { 
  Box, 
  CircularProgress, 
  Typography, 
  Alert,
  useTheme, 
  useMediaQuery,
  Card,
  CardContent,
  Chip
} from '@mui/material';
import { Bar } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
} from 'chart.js';
import axiosInstance from 'utils/axiosInstance';
import apiConfig from 'config/appConfig';
import moment from 'moment';

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
);

const ErrorRateChart = () => {
  const [loading, setLoading] = useState(true);
  const [chartData, setChartData] = useState(null);
  const [error, setError] = useState(null);
  const [errorSummary, setErrorSummary] = useState(null);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  useEffect(() => {
    const fetchLogs = async () => {
      setLoading(true);
      setError(null);
      try {
        const response = await axiosInstance.get(apiConfig.endpoints.requestLogs.paginated);
        const logs = response.data.requestLogs;

        // Process logs for error analysis
        const errorData = processErrorData(logs);
        
        if (!errorData.labels.length) {
          throw new Error("No error data available.");
        }

        setChartData({
          labels: errorData.labels,
          datasets: [
            {
              label: 'Error Count',
              data: errorData.errorCounts,
              backgroundColor: theme.palette.error.main,
              borderColor: theme.palette.error.dark,
              borderWidth: 1,
              borderRadius: 4,
              borderSkipped: false,
            },
            {
              label: 'Success Count',
              data: errorData.successCounts,
              backgroundColor: theme.palette.success.main,
              borderColor: theme.palette.success.dark,
              borderWidth: 1,
              borderRadius: 4,
              borderSkipped: false,
            }
          ],
        });

        setErrorSummary(errorData.summary);
      } catch (err) {
        console.error("Error fetching error rate data:", err);
        setError(err.message || "Failed to fetch error rate data.");
      } finally {
        setLoading(false);
      }
    };

    fetchLogs();
  }, [theme.palette.error.main, theme.palette.success.main, isMobile]);

  const processErrorData = (logs) => {
    // Group logs by day for the last 7 days
    const days = 7;
    const now = moment();
    const labels = [];
    const errorCounts = [];
    const successCounts = [];
    const errorTypes = {};

    for (let i = days - 1; i >= 0; i--) {
      const dayStart = moment(now).subtract(i, 'days').startOf('day');
      const dayEnd = moment(dayStart).endOf('day');
      
      labels.push(dayStart.format('MMM DD'));
      
      const dayLogs = logs.filter(log => {
        const logTime = moment(log.executedAt);
        return logTime.isBetween(dayStart, dayEnd, null, '[]');
      });

      const errors = dayLogs.filter(log => log.status === 'failure');
      const successes = dayLogs.filter(log => log.status === 'success');
      
      errorCounts.push(errors.length);
      successCounts.push(successes.length);

      // Track error types
      errors.forEach(error => {
        const errorType = error.errorType || 'Unknown';
        errorTypes[errorType] = (errorTypes[errorType] || 0) + 1;
      });
    }

    // Calculate summary statistics
    const totalRequests = logs.length;
    const totalErrors = logs.filter(log => log.status === 'failure').length;
    const errorRate = totalRequests > 0 ? (totalErrors / totalRequests * 100).toFixed(2) : 0;

    const summary = {
      totalRequests,
      totalErrors,
      errorRate: parseFloat(errorRate),
      topErrorTypes: Object.entries(errorTypes)
        .sort(([,a], [,b]) => b - a)
        .slice(0, 5)
        .map(([type, count]) => ({ type, count }))
    };

    return { labels, errorCounts, successCounts, summary };
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: isMobile ? 'bottom' : 'top',
        labels: {
          font: { size: isMobile ? 10 : 12 },
          usePointStyle: true,
          padding: isMobile ? 10 : 20,
        },
      },
      tooltip: {
        mode: 'index',
        intersect: false,
        titleFont: { size: isMobile ? 10 : 12 },
        bodyFont: { size: isMobile ? 10 : 12 },
        callbacks: {
          label: function(context) {
            return `${context.dataset.label}: ${context.parsed.y}`;
          }
        }
      },
    },
    scales: {
      x: {
        display: true,
        title: {
          display: !isMobile,
          text: 'Date',
          font: { size: isMobile ? 10 : 12 },
        },
        ticks: {
          font: { size: isMobile ? 8 : 10 },
          maxRotation: isMobile ? 45 : 0,
        },
        grid: {
          display: false,
        }
      },
      y: {
        display: true,
        title: {
          display: !isMobile,
          text: 'Request Count',
          font: { size: isMobile ? 10 : 12 },
        },
        ticks: {
          font: { size: isMobile ? 8 : 10 },
          beginAtZero: true,
        },
        grid: {
          color: theme.palette.divider,
        }
      },
    },
    interaction: {
      mode: 'nearest',
      axis: 'x',
      intersect: false,
    }
  };

  if (loading) {
    return (
      <Card sx={{ 
        height: 400,
        borderRadius: '6px',
        boxShadow: theme.shadows[2],
        '&:hover': { boxShadow: theme.shadows[4] }
      }}>
        <CardContent sx={{ 
          height: '100%', 
          display: 'flex', 
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          p: isMobile ? 2 : 3
        }}>
          <CircularProgress size={40} />
          <Typography variant="body2" sx={{ mt: 2, color: 'text.secondary' }}>
            Loading error rate data...
          </Typography>
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Card sx={{ 
        height: 400,
        boxShadow: theme.shadows[2],
        '&:hover': { boxShadow: theme.shadows[4] }
      }}>
        <CardContent sx={{ p: isMobile ? 2 : 3 }}>
          <Alert severity="error">{error}</Alert>
        </CardContent>
      </Card>
    );
  }

  if (!chartData) {
    return (
      <Card sx={{ 
        height: 400,
        boxShadow: theme.shadows[2],
        '&:hover': { boxShadow: theme.shadows[4] }
      }}>
        <CardContent sx={{ 
          height: '100%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          p: isMobile ? 2 : 3
        }}>
          <Typography variant="body1" color="text.secondary">
            No error rate data available
          </Typography>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card sx={{ 
      height: '100%',
      minHeight: 200,
      boxShadow: theme.shadows[2],
      '&:hover': { boxShadow: theme.shadows[4] },
      display: 'flex',
      flexDirection: 'column'
    }}>
      <CardContent sx={{ 
        p: isMobile ? 2 : 3,
        display: 'flex',
        flexDirection: 'column',
        flexGrow: 1
      }}>
        <Box sx={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center',
          mb: 1
        }}>
          <Typography 
            variant={isMobile ? "h6" : "h5"} 
            sx={{ fontWeight: 600 }}
          >
            Error Rate Analysis
          </Typography>
          {errorSummary && (
            <Chip
              label={`${errorSummary.errorRate}% Error Rate`}
              color={errorSummary.errorRate > 5 ? 'error' : errorSummary.errorRate > 2 ? 'warning' : 'success'}
              size={isMobile ? 'small' : 'medium'}
              variant="outlined"
            />
          )}
        </Box>
        
        <Box sx={{ 
          flexGrow: 1,
          position: 'relative',
          minHeight: 0
        }}>
          <Bar data={chartData} options={chartOptions} />
        </Box>

        {errorSummary && errorSummary.topErrorTypes.length > 0 && (
          <Box sx={{ mt: 1, pt: 1, borderTop: `1px solid ${theme.palette.divider}` }}>
            <Typography variant="caption" color="text.secondary" display="block" gutterBottom>
              Top Error Types:
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
              {errorSummary.topErrorTypes.map(({ type, count }) => (
                <Chip
                  key={type}
                  label={`${type}: ${count}`}
                  size="small"
                  variant="outlined"
                  color="error"
                />
              ))}
            </Box>
          </Box>
        )}
      </CardContent>
    </Card>
  );
};

export default ErrorRateChart; 